import { cx } from './cx';

export interface MoneyProps {
  amount: number;
  currency?: string;
  /** `in` = money arriving (soft magenta), `out` = money leaving or balances (gold). */
  direction?: 'in' | 'out' | 'neutral';
  /** Prefix + for incoming and − for outgoing amounts. */
  sign?: boolean;
  size?: 'small' | 'body' | 'title' | 'display';
  /** Number format; defaults to en-US because balances have always been shown as $1,234.56 in every UI language. */
  locale?: string;
  className?: string;
}

const SIZES = { small: 'text-small', body: 'text-body', title: 'text-title', display: 'text-display' } as const;
const COLORS = { in: 'text-amount-in', out: 'text-amount-out', neutral: '' } as const;
const MINUS = '−';

function formatter(locale: string, currency: string): Intl.NumberFormat {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' });
  } catch {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  }
}

/** Formatted money amount in tabular mono digits. */
export default function Money({ amount, currency = 'USD', direction = 'neutral', sign = false, size = 'body', locale, className }: MoneyProps) {
  const value = Number.isFinite(amount) ? amount : 0;
  const text = formatter(locale ?? 'en-US', currency).format(Math.abs(value));
  let prefix = value < 0 ? MINUS : '';
  if (sign && direction === 'in') prefix = '+';
  if (sign && direction === 'out') prefix = MINUS;
  return (
    <span className={cx('font-money font-semibold tabular-nums whitespace-nowrap', SIZES[size], COLORS[direction], className)}>
      {prefix}
      {text}
    </span>
  );
}
